import { createRouter, createWebHistory } from 'vue-router'
import { fromAppToStorage } from '@shared/helpers/isFromApp'
import Tracker from '@shared/Tracker'
import useAuthStore from '@shared/stores/useAuthStore.js'
import appModuleRouter from '@eu/app/router/appModule/index.js'
import assessmentModuleRouter from '@shared/modules/assessment/router/index.js'
import env from '@shared/env.js'
import useFeatureFlagStore from '@shared/stores/useFeatureFlagStore'
import enableViewTransitions from '@shared/router/enableViewTransitions.js'
import moduleRouteMatches from '@shared/router/moduleRouteMatches'
import oneTimeTokenGuard from '@shared/router/oneTimeTokenGuard'
import actionLinkRoutes from '@eu/app/actionLinks/routes.js'
import { takePendingLpPainLocation } from '@shared/lpPainLocation.js'

const initRouter = () => {
  const authStore = useAuthStore()
  const featureFlagStore = useFeatureFlagStore()

  const routerModules = [appModuleRouter, assessmentModuleRouter]

  const router = createRouter({
    routes: [
      // Action link pages are msk-eu only
      ...(env.isMskEu ? actionLinkRoutes : []),
      // aggregate all routes from all modules
      ...routerModules.flatMap((module) => module.routes),
      // define a catch-all route
      {
        path: '/:pathMatch(.*)*',
        redirect: {
          name: appModuleRouter.ROUTE.SIGN_UP,
          params: {},
        },
      },
    ],
    linkActiveClass: 'open active',
    scrollBehavior: (to, from, savedPosition) =>
      savedPosition || { x: 0, y: 0 },
    history: createWebHistory(env.VITE_PUBLIC_PATH),
  })

  router.beforeEach(async (routeTo, routeFrom) => {
    featureFlagStore.applyQueryOverrides(routeTo.query)

    if (routeTo.query.from_app) {
      fromAppToStorage()

      const { client_app_id, client_version } = routeTo.query

      authStore.hostClientAppId = Array.isArray(client_app_id)
        ? client_app_id[0]
        : client_app_id
      authStore.hostClientVersion = Array.isArray(client_version)
        ? client_version[0]
        : client_version
    }

    const tokenTarget = await oneTimeTokenGuard(routeTo)
    if (tokenTarget !== undefined) return tokenTarget

    if (!authStore.isAuthenticated) {
      featureFlagStore.assignPreSignupFlags()
    }

    for (const module of routerModules) {
      // Match against the resolved route tree so nested child routes can run module hooks.
      if (moduleRouteMatches(module.routes, routeTo)) {
        return module.hooks(routeTo, routeFrom)
      }
    }
  })

  router.afterEach((routeTo) => {
    if (routeTo.meta && routeTo.meta.name) {
      Tracker.trackViewScreen(routeTo.meta.name)
    }
  })

  // The LP pain location is for the first navigation only: one that never
  // reaches the pre-signup entry guard (e.g. a signed-in visitor) drops it.
  router.afterEach(() => {
    takePendingLpPainLocation()
  })

  return enableViewTransitions(router)
}

export default initRouter
